import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import cytoscape, { Core, NodeSingular, EventObject } from 'cytoscape';
import dagre from 'cytoscape-dagre';
import { X } from 'lucide-react';

dagre(cytoscape);

interface Finding {
  id?: string;
  title: string;
  severity: string;
  scan_type?: string;
  description?: string;
  cvss_v4?: { score: number };
  cvss_v4_score?: number;
}

interface AttackChainStep {
  step: number;
  module: string;
  finding: string;
}

interface AttackChain {
  title: string;
  severity: string;
  business_impact?: string;
  steps: AttackChainStep[];
}

interface RiskPropagationGraphProps {
  findings: Finding[];
  attackChains: AttackChain[];
  target: string;
}

interface NodeDetail {
  label: string;
  severity: string;
  riskScore: number;
  inDegree: number;
  outDegree: number;
  reachable: string[];
}

const SEVERITY_WEIGHT: Record<string, number> = {
  critical: 10,
  high: 7,
  medium: 4,
  low: 2,
  info: 0.5,
};

const SEVERITY_NODE_COLOR: Record<string, string> = {
  critical: '#dc2626',
  high: '#ea580c',
  medium: '#eab308',
  low: '#3b82f6',
  info: '#6b7280',
};

function getCvss(f: Finding): number {
  if (f.cvss_v4?.score != null) return f.cvss_v4.score;
  if (f.cvss_v4_score != null) return f.cvss_v4_score;
  return SEVERITY_WEIGHT[f.severity?.toLowerCase()] ?? 1;
}

function difficultyToWeight(severity: string): number {
  switch (severity?.toLowerCase()) {
    case 'critical': return 1;
    case 'high': return 2;
    case 'medium': return 3;
    case 'low': return 4;
    case 'info': return 5;
    default: return 3;
  }
}

function weightToColor(weight: number): string {
  if (weight <= 1) return '#ef4444';
  if (weight <= 2) return '#f97316';
  if (weight <= 3) return '#eab308';
  if (weight <= 4) return '#84cc16';
  return '#22c55e';
}

function buildGraphData(
  findings: Finding[],
  chains: AttackChain[],
  target: string,
): { nodes: cytoscape.ElementDefinition[]; edges: cytoscape.ElementDefinition[] } {
  const nodeMap = new Map<string, { severity: string; cvss: number; connectivity: number }>();
  const edgeList: { source: string; target: string; weight: number; chainTitle: string }[] = [];

  nodeMap.set(target, { severity: 'target', cvss: 0, connectivity: 0 });

  for (const f of findings) {
    const key = f.title;
    if (!nodeMap.has(key)) {
      nodeMap.set(key, { severity: f.severity, cvss: getCvss(f), connectivity: 0 });
    }
  }

  for (const chain of chains) {
    const steps = chain.steps ?? [];
    if (steps.length === 0) continue;

    const firstStep = steps[0];
    const firstKey = firstStep.finding || firstStep.module;
    if (!nodeMap.has(firstKey)) {
      nodeMap.set(firstKey, { severity: chain.severity, cvss: 4, connectivity: 0 });
    }
    edgeList.push({
      source: target,
      target: firstKey,
      weight: difficultyToWeight(chain.severity),
      chainTitle: chain.title,
    });

    for (let i = 0; i < steps.length - 1; i++) {
      const srcKey = steps[i].finding || steps[i].module;
      const tgtKey = steps[i + 1].finding || steps[i + 1].module;
      if (!nodeMap.has(srcKey)) {
        nodeMap.set(srcKey, { severity: chain.severity, cvss: 3, connectivity: 0 });
      }
      if (!nodeMap.has(tgtKey)) {
        nodeMap.set(tgtKey, { severity: chain.severity, cvss: 3, connectivity: 0 });
      }
      edgeList.push({
        source: srcKey,
        target: tgtKey,
        weight: difficultyToWeight(chain.severity),
        chainTitle: chain.title,
      });
    }

    if (chain.business_impact && steps.length > 0) {
      const lastStep = steps[steps.length - 1];
      const lastKey = lastStep.finding || lastStep.module;
      const impactKey = `impact:${chain.title}`;
      if (!nodeMap.has(impactKey)) {
        nodeMap.set(impactKey, { severity: 'critical', cvss: 10, connectivity: 0 });
      }
      edgeList.push({
        source: lastKey,
        target: impactKey,
        weight: 1,
        chainTitle: chain.title,
      });
    }
  }

  for (const edge of edgeList) {
    const src = nodeMap.get(edge.source);
    const tgt = nodeMap.get(edge.target);
    if (src) src.connectivity++;
    if (tgt) tgt.connectivity++;
  }

  const maxConnectivity = Math.max(1, ...Array.from(nodeMap.values()).map((n) => n.connectivity));

  const nodes: cytoscape.ElementDefinition[] = Array.from(nodeMap.entries()).map(
    ([id, data]) => {
      const baseSize = 30;
      const sizeScale = 40 * (data.connectivity / maxConnectivity);
      const size = baseSize + sizeScale;
      const isTarget = id === target;
      const isImpact = id.startsWith('impact:');
      const bgColor = isTarget
        ? '#7c3aed'
        : isImpact
          ? '#991b1b'
          : SEVERITY_NODE_COLOR[data.severity?.toLowerCase()] ?? '#6b7280';

      return {
        data: {
          id,
          label: isImpact ? id.replace('impact:', '') : id,
          severity: data.severity,
          cvss: data.cvss,
          connectivity: data.connectivity,
          size,
          bgColor,
          borderColor: isTarget ? '#6d28d9' : isImpact ? '#7f1d1d' : bgColor,
          isTarget,
          isImpact,
        },
      };
    },
  );

  const seenEdges = new Set<string>();
  const edges: cytoscape.ElementDefinition[] = [];
  for (const e of edgeList) {
    const edgeId = `${e.source}→${e.target}`;
    if (seenEdges.has(edgeId)) continue;
    seenEdges.add(edgeId);
    edges.push({
      data: {
        id: edgeId,
        source: e.source,
        target: e.target,
        weight: e.weight,
        width: Math.max(1, 6 - e.weight),
        lineColor: weightToColor(e.weight),
        chainTitle: e.chainTitle,
      },
    });
  }

  return { nodes, edges };
}

function findLowestWeightPath(cy: Core, target: string): Set<string> {
  const impactNodes = cy.nodes().filter((n) => n.data('isImpact'));
  if (impactNodes.length === 0) return new Set();

  const sourceNode = cy.getElementById(target);
  if (sourceNode.empty()) return new Set();

  let bestPath: string[] = [];
  let bestWeight = Infinity;

  for (const impactNode of impactNodes.toArray()) {
    const dijkstra = cy.elements().dijkstra({
      root: sourceNode,
      weight: (edge) => (edge as unknown as cytoscape.EdgeSingular).data('weight') as number,
      directed: true,
    });
    const node = impactNode as unknown as cytoscape.NodeSingular;
    const dist = dijkstra.distanceTo(node);
    if (dist < bestWeight) {
      bestWeight = dist;
      const path = dijkstra.pathTo(node);
      bestPath = path.map((el) => el.id());
    }
  }

  return new Set(bestPath);
}

export default function RiskPropagationGraph({
  findings,
  attackChains,
  target,
}: RiskPropagationGraphProps): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const cyRef = useRef<Core | null>(null);
  const [selectedNode, setSelectedNode] = useState<NodeDetail | null>(null);

  const graphData = useMemo(
    () => buildGraphData(findings ?? [], attackChains ?? [], target),
    [findings, attackChains, target],
  );

  const handleNodeTap = useCallback((evt: EventObject) => {
    const node = evt.target as NodeSingular;
    const cy = cyRef.current;
    if (!cy) return;

    const reachable: string[] = [];
    const visited = new Set<string>();
    const queue = [node.id()];
    visited.add(node.id());

    while (queue.length > 0) {
      const current = queue.shift()!;
      const outgoing = cy.getElementById(current).outgoers('node');
      for (const neighbor of outgoing.toArray()) {
        const nid = neighbor.id();
        if (!visited.has(nid)) {
          visited.add(nid);
          reachable.push(neighbor.data('label') as string);
          queue.push(nid);
        }
      }
    }

    setSelectedNode({
      label: node.data('label') as string,
      severity: node.data('severity') as string,
      riskScore: Math.round(
        ((node.data('cvss') as number) ?? 0) *
        ((node.data('connectivity') as number) ?? 1),
      ),
      inDegree: node.indegree(false),
      outDegree: node.outdegree(false),
      reachable,
    });
  }, []);

  useEffect(() => {
    if (!containerRef.current) return;
    if (graphData.nodes.length === 0) return;

    const cy = cytoscape({
      container: containerRef.current,
      elements: [...graphData.nodes, ...graphData.edges],
      style: [
        {
          selector: 'node',
          style: {
            label: 'data(label)',
            width: 'data(size)',
            height: 'data(size)',
            'background-color': 'data(bgColor)',
            'border-width': 2,
            'border-color': 'data(borderColor)',
            color: '#e2e8f0',
            'font-size': 9,
            'text-wrap': 'ellipsis',
            'text-max-width': '100px',
            'text-valign': 'bottom',
            'text-margin-y': 6,
            'text-background-color': '#0f172a',
            'text-background-opacity': 0.7,
            'text-background-padding': 2 as unknown as cytoscape.Css.PropertyValueNode<string>,
          },
        },
        {
          selector: 'edge',
          style: {
            width: 'data(width)',
            'line-color': 'data(lineColor)',
            'target-arrow-color': 'data(lineColor)',
            'target-arrow-shape': 'triangle',
            'curve-style': 'bezier',
            opacity: 0.7,
          },
        },
        {
          selector: 'node[?isTarget]',
          style: {
            shape: 'diamond',
            'border-width': 3,
          },
        },
        {
          selector: 'node[?isImpact]',
          style: {
            shape: 'rectangle',
            'border-width': 3,
            'border-style': 'double',
          },
        },
        {
          selector: '.highlighted-path',
          style: {
            'line-color': '#ef4444',
            'target-arrow-color': '#ef4444',
            width: 5,
            opacity: 1,
            'z-index': 10,
          },
        },
        {
          selector: '.highlighted-node',
          style: {
            'border-color': '#ef4444',
            'border-width': 4,
            'z-index': 10,
          },
        },
      ],
      layout: {
        name: 'dagre',
        rankDir: 'TB',
        nodeSep: 60,
        rankSep: 80,
        padding: 30,
      } as cytoscape.LayoutOptions,
      userZoomingEnabled: true,
      userPanningEnabled: true,
      boxSelectionEnabled: false,
    });

    cyRef.current = cy;

    const pathIds = findLowestWeightPath(cy, target);
    for (const elId of pathIds) {
      const el = cy.getElementById(elId) as cytoscape.SingularElementReturnValue;
      if (el.isNode()) {
        el.addClass('highlighted-node');
      } else {
        (el as cytoscape.EdgeSingular).addClass('highlighted-path');
      }
    }

    cy.on('tap', 'node', handleNodeTap);

    return () => {
      cy.destroy();
      cyRef.current = null;
    };
  }, [graphData, target, handleNodeTap]);

  const severityBadgeColor: Record<string, string> = {
    critical: '#dc2626',
    high: '#ea580c',
    medium: '#eab308',
    low: '#3b82f6',
    info: '#6b7280',
    target: '#7c3aed',
  };

  return (
    <div
      style={{
        background: '#0f172a',
        borderRadius: 12,
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      <div
        style={{
          padding: '14px 18px',
          borderBottom: '1px solid #1e293b',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div>
          <h3 style={{ color: '#f1f5f9', fontSize: 15, fontWeight: 600, margin: 0 }}>
            Risk Propagation Graph
          </h3>
          <p style={{ color: '#64748b', fontSize: 11, margin: '4px 0 0' }}>
            Edge width = exploitation ease. Red path = least resistance route.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#22c55e', display: 'inline-block' }} />
            <span style={{ color: '#94a3b8', fontSize: 10 }}>Hard</span>
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#eab308', display: 'inline-block' }} />
            <span style={{ color: '#94a3b8', fontSize: 10 }}>Medium</span>
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#ef4444', display: 'inline-block' }} />
            <span style={{ color: '#94a3b8', fontSize: 10 }}>Trivial</span>
          </span>
        </div>
      </div>

      <div ref={containerRef} style={{ width: '100%', height: 520, background: '#0f172a' }} />

      {selectedNode && (
        <div
          style={{
            position: 'absolute',
            top: 60,
            right: 16,
            width: 260,
            background: '#1e293b',
            border: '1px solid #334155',
            borderRadius: 10,
            padding: 16,
            zIndex: 20,
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
            <h4 style={{ color: '#f1f5f9', fontSize: 13, fontWeight: 600, margin: 0, flex: 1, lineHeight: 1.3 }}>
              {selectedNode.label}
            </h4>
            <button
              onClick={() => setSelectedNode(null)}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: 2,
                marginLeft: 8,
                flexShrink: 0,
              }}
            >
              <X style={{ width: 14, height: 14, color: '#94a3b8' }} />
            </button>
          </div>

          <span
            style={{
              display: 'inline-block',
              padding: '2px 8px',
              borderRadius: 4,
              fontSize: 10,
              fontWeight: 600,
              color: '#fff',
              background: severityBadgeColor[selectedNode.severity?.toLowerCase()] ?? '#6b7280',
              textTransform: 'uppercase',
              marginBottom: 12,
            }}
          >
            {selectedNode.severity}
          </span>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
            <div style={{ background: '#0f172a', borderRadius: 6, padding: '8px 10px' }}>
              <div style={{ color: '#94a3b8', fontSize: 9, marginBottom: 2 }}>Risk Score</div>
              <div style={{ color: '#f59e0b', fontSize: 18, fontWeight: 700 }}>{selectedNode.riskScore}</div>
            </div>
            <div style={{ background: '#0f172a', borderRadius: 6, padding: '8px 10px' }}>
              <div style={{ color: '#94a3b8', fontSize: 9, marginBottom: 2 }}>Connections</div>
              <div style={{ color: '#a78bfa', fontSize: 18, fontWeight: 700 }}>
                {selectedNode.inDegree + selectedNode.outDegree}
              </div>
            </div>
          </div>

          {selectedNode.reachable.length > 0 && (
            <div>
              <div style={{ color: '#94a3b8', fontSize: 10, marginBottom: 6, fontWeight: 600 }}>
                Reachable from here ({selectedNode.reachable.length})
              </div>
              <div style={{ maxHeight: 120, overflowY: 'auto' }}>
                {selectedNode.reachable.map((r) => (
                  <div
                    key={r}
                    style={{
                      color: '#cbd5e1',
                      fontSize: 10,
                      padding: '3px 6px',
                      background: '#0f172a',
                      borderRadius: 4,
                      marginBottom: 3,
                      lineHeight: 1.3,
                    }}
                  >
                    {r}
                  </div>
                ))}
              </div>
            </div>
          )}

          {selectedNode.reachable.length === 0 && (
            <div style={{ color: '#64748b', fontSize: 10, fontStyle: 'italic' }}>
              Terminal node -- no further propagation.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
